'use client';

import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';

// Página pública de status (UptimeRobot). É pública e estável -> constante.
const STATUS_URL = 'https://stats.uptimerobot.com/tPiLSsGkGI';

type Status = { total: number; up: number; down: number; paused: number };

// Rodapé da sidebar: espelha o resumo do UptimeRobot (bolinha + Ativo/Inativo/
// Pausado) e, ao clicar, abre a página de status em nova aba.
export function StatusFooter() {
  const [s, setS] = useState<Status | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch('/api/status')
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((d) => {
          if (!alive) return;
          if (d && typeof d.total === 'number') {
            setS(d);
            setErr(false);
          } else {
            setErr(true);
          }
        })
        .catch(() => {
          if (alive) setErr(true);
        });

    load();
    const t = setInterval(load, 60_000); // atualiza a cada 60s
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Se a API não estiver configurada/indisponível, não renderiza nada.
  if (err && s == null) return null;

  const down = s?.down ?? 0;
  const dot = s == null ? '#9ca3af' : down > 0 ? '#ef4444' : '#22c55e';
  const label =
    s == null
      ? 'Carregando status…'
      : down > 0
        ? `${down} serviço${down > 1 ? 's' : ''} fora do ar`
        : 'Todos os sistemas operacionais';

  return (
    <a
      href={STATUS_URL}
      target="_blank"
      rel="noopener noreferrer"
      title="Abrir página de status (nova aba)"
      className="group flex flex-col gap-1 rounded-lg border border-fd-border bg-fd-card p-2.5 text-xs transition-colors hover:bg-fd-accent"
    >
      <span className="flex items-center gap-2">
        <span className="relative flex size-2.5 shrink-0">
          {s != null && down === 0 && (
            <span
              className="absolute inline-flex size-full animate-ping rounded-full opacity-60"
              style={{ backgroundColor: dot }}
            />
          )}
          <span
            className="relative inline-flex size-2.5 rounded-full"
            style={{ backgroundColor: dot }}
          />
        </span>
        <span className="font-medium text-fd-foreground">{label}</span>
        <ExternalLink className="ms-auto size-3.5 shrink-0 text-fd-muted-foreground opacity-60 transition-opacity group-hover:opacity-100" />
      </span>

      {s != null && (
        <span className="ps-[1.125rem] text-fd-muted-foreground">
          <span className="text-emerald-500">Ativo {s.up}</span>
          {' · '}
          <span className={down > 0 ? 'text-red-500' : undefined}>Inativo {s.down}</span>
          {s.paused > 0 && (
            <>
              {' · '}
              <span>Pausado {s.paused}</span>
            </>
          )}
        </span>
      )}
    </a>
  );
}
